/**
 * Öffentliches Kunden-Upload-Portal (Phase 2)
 * Token-Link aus der E-Mail: /belege/<token>
 * Sicherheitsabfrage: Nachname + Geburtsdatum.
 */
import { useState, useRef, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Upload, Loader2, CheckCircle2, FileText, ShieldCheck, ExternalLink, AlertCircle, Info, Smartphone, Camera } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import DealerBrandLogo from "../components/brand/DealerBrandLogo";
import api from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDateDE = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return isNaN(d) ? String(iso).slice(0, 10) : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

function PortalRow({ token, lastName, birthDate, doc, onChanged, highlight, staffMode }) {
    const [busy, setBusy] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);
    const camRef = useRef(null);
    const rowRef = useRef(null);

    // Jun-2026 (User-Wunsch) — Markierte Zeile (per QR vom Desktop angesprungen)
    // wird hervorgehoben & in den Sichtbereich gescrollt.
    useEffect(() => {
        if (highlight && rowRef.current) {
            rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [highlight]);

    // Jul-2026 (User-Wunsch) — Mitarbeiter-Modus: QR am Dokument gescannt →
    // Kamera öffnet sich SOFORT (ohne Sicherheitsabfrage, ohne weiteren Klick).
    useEffect(() => {
        if (staffMode && highlight && !doc.uploaded && camRef.current) {
            const t = setTimeout(() => { try { camRef.current.click(); } catch { /* Browser blockt — Button bleibt */ } }, 400);
            return () => clearTimeout(t);
        }
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [staffMode, highlight]);

    const upload = async (fileList, fromCamera = false) => {
        // Jul-2026 (User-Wunsch) — MEHRERE Fotos/Dateien je Beleg (z. B. Ausweis,
        // mehrseitige Verträge) — werden an den Beleg angehängt.
        const files = Array.from(fileList || []).filter(Boolean);
        if (!files.length) return;
        for (const f of files) {
            if (f.size > 10 * 1024 * 1024) { toast.error(`„${f.name}" zu groß (max 10 MB)`); return; }
        }
        setBusy(true);
        try {
            let r;
            for (const file of files) {
                const fd = new FormData();
                fd.append("code", doc.code);
                fd.append("file", file);
                if (staffMode) {
                    r = await api.post(`/belege/${token}/staff-upload`, fd);
                } else {
                    fd.append("last_name", lastName);
                    fd.append("birth_date", birthDate);
                    r = await axios.post(`${API}/public/belege/${token}/upload`, fd);
                }
            }
            // Jul-2026 (User-Wunsch) — nach jedem Kamera-Foto direkt das nächste
            // Foto anbieten (mehrseitige Dokumente ohne Umwege).
            toast.success(
                files.length > 1 ? `${files.length} Dateien zu „${doc.label}" hochgeladen` : `„${doc.label}" hochgeladen`,
                fromCamera
                    ? { duration: 8000, action: { label: "+ Weiteres Foto", onClick: () => camRef.current?.click() } }
                    : undefined,
            );
            onChanged?.(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload fehlgeschlagen");
        } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; if (camRef.current) camRef.current.value = ""; }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (busy) return;
        const files = e.dataTransfer?.files;
        if (files?.length) upload(files);
    };

    // Jun-2026 (User-Wunsch) — kleiner QR je Dokument: führt das HANDY des Kunden
    // direkt (vor-verifiziert) zu genau diesem Upload (Kamera-Aufnahme möglich).
    const qrValue = `${window.location.origin}/belege/${token}?doc=${encodeURIComponent(doc.code)}&ln=${encodeURIComponent(lastName)}&bd=${encodeURIComponent(birthDate)}`;

    const q = doc.quality;
    const qBad = Boolean(doc.uploaded && q && q.checked && q.ok === false);
    const qGood = Boolean(doc.uploaded && q && q.checked && q.ok === true);
    const qPending = Boolean(doc.uploaded && q && q.pending);

    return (
        <div
            ref={rowRef}
            onDragOver={(e) => { if (!busy) { e.preventDefault(); setDragOver(true); } }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`flex flex-col gap-2 px-4 py-3 border rounded-lg transition-colors ${
                qBad ? "bg-amber-950/40 border-amber-700"
                : (doc.uploaded || doc.required === false) ? "bg-emerald-950/40 border-emerald-800"
                : dragOver ? "bg-[#FF8300]/10 border-[#FF8300] border-dashed"
                : highlight ? "bg-amber-950/40 border-amber-600 ring-2 ring-amber-500/50"
                : "bg-[#161618] border-[#2A2A2E]"
            }`}
            data-testid={`portal-row-${doc.code}`}
        >
            {/* Versteckte Inputs immer gerendert → „Erneut aufnehmen" funktioniert auch nach Upload. */}
            <input ref={inputRef} type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={(e) => upload(e.target.files)} data-testid={`portal-input-${doc.code}`} />
            <input ref={camRef} type="file" multiple accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files, true)} data-testid={`portal-camera-input-${doc.code}`} />
            <div className="flex items-center gap-3">
                <FileText className={`w-5 h-5 shrink-0 ${qBad ? "text-amber-500" : (doc.uploaded || doc.required === false) ? "text-emerald-500" : "text-gray-500"}`} />
                    <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-white">{doc.label}</div>
                    {doc.note ? <div className="text-xs text-gray-400">{doc.note}</div> : null}
                    {doc.uploaded
                        ? <div className={`text-xs flex items-center gap-1 mt-0.5 ${qBad ? "text-amber-400" : "text-emerald-400"}`} data-testid={`portal-date-${doc.code}`}>
                            {qBad ? <AlertCircle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                            {qBad ? "Foto-Qualität prüfen" : <>Hochgeladen · {fmtDateDE(doc.uploaded_at)}</>}
                            {doc.pages > 1 ? <span className="text-gray-400 font-semibold ml-1">· {doc.pages} Seiten</span> : null}
                            {qPending ? <span className="text-gray-400 font-semibold ml-1 inline-flex items-center gap-1">· <Loader2 className="w-3 h-3 animate-spin" /> KI prüft…</span> : null}
                            {qGood ? <span className="text-emerald-400 font-semibold ml-1">· KI-geprüft ✓</span> : null}
                          </div>
                        : doc.required === false
                        ? <div className="text-xs text-emerald-400 flex items-center gap-1 mt-0.5" data-testid={`portal-optional-ok-${doc.code}`}>
                            <CheckCircle2 className="w-3.5 h-3.5" /> Optional — kein Upload nötig
                          </div>
                        : <><div className="text-[11px] text-gray-500 mt-0.5 hidden sm:block">Datei(en) hierher ziehen oder hochladen — mehrere Seiten möglich</div>
                           <div className="text-[11px] text-gray-500 mt-0.5 sm:hidden flex items-center gap-1"><Camera className="w-3 h-3" /> Direkt fotografieren — mehrere Fotos möglich</div></>}
                </div>
                {/* Sehr kleiner QR-Code → Handy direkt zum Upload dieses Dokuments */}
                {!doc.uploaded && (
                    <div className="hidden sm:flex flex-col items-center shrink-0" title="Mit dem Handy scannen, um dieses Dokument direkt per Kamera hochzuladen" data-testid={`portal-qr-${doc.code}`}>
                        <div className="p-1 bg-white border border-gray-200 rounded">
                            <QRCodeSVG value={qrValue} size={40} level="M" includeMargin={false} />
                        </div>
                        <span className="text-[8px] text-gray-400 flex items-center gap-0.5 mt-0.5"><Smartphone className="w-2 h-2" /> Handy</span>
                    </div>
                )}
                {doc.uploaded ? (
                    <div className="flex items-center gap-1.5 shrink-0">
                        {qBad && (
                            <Button type="button" size="sm" onClick={() => camRef.current?.click()} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid={`portal-retake-${doc.code}`}>
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-1" /> Erneut aufnehmen</>}
                            </Button>
                        )}
                        {/* Jul-2026 (User-Wunsch) — weitere Seiten/Fotos nachreichen. */}
                        <Button type="button" size="sm" onClick={() => camRef.current?.click()} disabled={busy}
                            className="sm:hidden bg-[#FF8300] hover:bg-[#ff9526] text-black" title="Weitere Seite fotografieren" data-testid={`portal-addpage-cam-${doc.code}`}>
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-1" /> + Foto</>}
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy}
                            className="hidden sm:inline-flex border-2 border-[#3A3A3E] bg-transparent text-gray-200 hover:bg-[#1F1F23] hover:text-white" title="Weitere Seite/Datei hochladen" data-testid={`portal-addpage-${doc.code}`}>
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" /> + Seite</>}
                        </Button>
                        <a href={`${PUBLIC_BASE}${doc.url}`} target="_blank" rel="noopener noreferrer" data-testid={`portal-view-${doc.code}`}>
                            <Button type="button" size="sm" variant="outline" className="border-2 border-[#3A3A3E] bg-transparent text-gray-200 hover:bg-[#1F1F23] hover:text-white"><ExternalLink className="w-4 h-4" /></Button>
                        </a>
                    </div>
                ) : (
                    <>
                        {/* „Hochladen" = Datei/Galerie · „Foto/Scannen" = direkt Rückkamera (Handy). */}
                        <Button type="button" size="sm" onClick={() => camRef.current?.click()} disabled={busy} className="sm:hidden bg-orange-500 hover:bg-orange-600 shrink-0" data-testid={`portal-camera-${doc.code}`}>
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-1" /> Foto</>}
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} className="border-2 border-[#3A3A3E] bg-transparent text-gray-200 hover:bg-[#1F1F23] hover:text-white shrink-0" data-testid={`portal-upload-${doc.code}`}>
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" /> Hochladen</>}
                        </Button>
                    </>
                )}
            </div>
            {qBad && (
                <div className="bg-[#1F1F23] border border-amber-700 rounded-md px-3 py-2 text-xs text-amber-300" data-testid={`portal-quality-warning-${doc.code}`}>
                    <div className="font-bold flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Bitte erneut aufnehmen</div>
                    <div className="mt-0.5 text-amber-200/90">{q.message || "Das Foto ist unklar oder unvollständig. Bitte fotografieren Sie das Dokument erneut."}</div>
                    {q.issues?.length ? <ul className="list-disc ml-4 mt-1 text-amber-400">{q.issues.map((it, i) => <li key={i}>{it}</li>)}</ul> : null}
                </div>
            )}
        </div>
    );
}

/**
 * Jun-2026 (User-Wunsch) — Geburtsdatum-Eingabe nur über Zifferntasten (TT.MM.JJJJ),
 * ohne Kalender-Picker. Akzeptiert/zeigt deutsches Format; das Backend normalisiert
 * sowohl TT.MM.JJJJ als auch ISO.
 */
function NumericDateInput({ value, onChange, ...props }) {
    const toDisplay = (v) => {
        if (!v) return "";
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) { const [y, m, d] = v.split("-"); return `${d}.${m}.${y}`; }
        return v;
    };
    const [text, setText] = useState(toDisplay(value));
    const handle = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 8); // TTMMJJJJ
        let out = digits;
        if (digits.length > 4) out = `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
        else if (digits.length > 2) out = `${digits.slice(0, 2)}.${digits.slice(2)}`;
        setText(out);
        onChange(out);
    };
    return (
        <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="bday"
            placeholder="TT.MM.JJJJ"
            maxLength={10}
            value={text}
            onChange={handle}
            {...props}
        />
    );
}

function BrandLogo({ className = "", branding }) {
    return (
        <div className={`flex items-center justify-center ${className}`} data-testid="portal-logo">
            <DealerBrandLogo
                variant="dark"
                logoUrl={branding?.dealer_logo}
                companyName={branding?.company_name || branding?.dealer_name}
                imgClassName="h-10 max-w-[200px]"
                textClassName="text-xl"
            />
        </div>
    );
}

export default function BelegePortalPage() {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const autoDoc = searchParams.get("doc") || "";
    const [lastName, setLastName] = useState(searchParams.get("ln") || "");
    const [birthDate, setBirthDate] = useState(searchParams.get("bd") || "");
    const [verifying, setVerifying] = useState(false);
    const [data, setData] = useState(null);
    const [consent, setConsent] = useState(false);
    const [branding, setBranding] = useState(null);
    const [staffMode, setStaffMode] = useState(false);

    // Jul-2026 (User-Wunsch) — eingeloggte Mitarbeiter/Händler überspringen die
    // Sicherheitsabfrage komplett (QR scannen → Kamera → hochladen).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.post(`/belege/${token}/staff-verify`);
                if (!cancelled && r.data?.ok) { setStaffMode(true); setData(r.data); }
            } catch { /* kein eingeloggter Mitarbeiter → normale Sicherheitsabfrage */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Händler-Branding (Firmenname + Logo) für den Login-Screen laden.
    useEffect(() => {
        axios.get(`${API}/public/belege/${token}/branding`).then((r) => setBranding(r.data)).catch(() => {});
    }, [token]);

    const verify = async (ln = lastName, bd = birthDate) => {
        if (!ln.trim() || !bd) { toast.error("Bitte Nachname und Geburtsdatum eingeben"); return; }
        setVerifying(true);
        try {
            const r = await axios.post(`${API}/public/belege/${token}/verify`, { last_name: ln, birth_date: bd });
            setData(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Anmeldung fehlgeschlagen");
        } finally { setVerifying(false); }
    };

    // Jun-2026 (User-Wunsch) — QR vom Desktop: Handy öffnet vor-verifiziert (ln/bd
    // in der URL) und springt direkt zum gewünschten Dokument.
    useEffect(() => {
        const ln = searchParams.get("ln");
        const bd = searchParams.get("bd");
        if (ln && bd && !data) verify(ln, bd);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Jul-2026 (User-Wunsch) — KI-Prüfung läuft im Hintergrund: Status nachladen,
    // solange eine Prüfung aussteht (Kunde erhält den Hinweis automatisch).
    useEffect(() => {
        if (!data) return undefined;
        const anyPending = (data.documents || []).some((d) => d.uploaded && d.quality?.pending);
        if (!anyPending) return undefined;
        const t = setTimeout(async () => {
            try {
                const r = staffMode
                    ? await api.post(`/belege/${token}/staff-verify`)
                    : await axios.post(`${API}/public/belege/${token}/verify`, { last_name: lastName, birth_date: birthDate });
                setData(r.data);
            } catch { /* still */ }
        }, 6000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    if (!data) {
        return (
            <div className="min-h-screen bg-[#0B0B0D] flex items-center justify-center px-4">
                <div className="w-full max-w-md bg-[#161618] border border-[#2A2A2E] rounded-xl shadow-xl p-6" data-testid="portal-gate">
                    <BrandLogo className="mb-5" branding={data || branding} />
                    <div className="flex items-center gap-2 mb-1"><ShieldCheck className="w-6 h-6 text-[#FF8300]" /><h1 className="text-xl font-black text-white">Sicherer Unterlagen-Upload</h1></div>
                    <p className="text-sm text-gray-400 mb-5">Bitte bestätigen Sie Ihre Identität, um Ihre Unterlagen hochzuladen.</p>
                    <div className="space-y-3">
                        <div><Label className="text-xs text-gray-300">Nachname</Label><Input value={lastName} onChange={(e) => setLastName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && consent) verify(); }} className="mt-1 bg-[#1F1F23] border-[#3A3A3E] text-white placeholder:text-gray-500" data-testid="portal-last-name" /></div>
                        <div><Label className="text-xs text-gray-300">Geburtsdatum</Label><NumericDateInput value={birthDate} onChange={setBirthDate} onKeyDown={(e) => { if (e.key === "Enter" && consent) verify(); }} className="mt-1 bg-[#1F1F23] border-[#3A3A3E] text-white placeholder:text-gray-500" data-testid="portal-birth-date" /></div>
                        <label className="flex items-start gap-2 text-xs text-gray-300 cursor-pointer" data-testid="portal-consent-label">
                            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FF8300] shrink-0" data-testid="portal-consent-checkbox" />
                            <span>Ich habe die <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-[#FF8300] underline">AGB</a> und die <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#FF8300] underline">Datenschutzerklärung</a> gelesen und akzeptiere sie.</span>
                        </label>
                        <Button onClick={() => verify()} disabled={verifying || !consent} className="w-full bg-[#FF8300] hover:bg-[#ff9526] text-black font-bold disabled:opacity-50" data-testid="portal-verify-btn">
                            {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Anmelden & Unterlagen anzeigen"}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const pct = data.total ? Math.round((data.done / data.total) * 100) : 0;
    return (
        <div className="min-h-screen bg-[#0B0B0D] py-8 px-4">
            <div className="max-w-2xl mx-auto" data-testid="portal-content">
                <BrandLogo className="mb-5" branding={data || branding} />
                <div className="bg-[#161618] border border-[#2A2A2E] rounded-xl shadow-xl p-6 mb-4">
                    <div className="text-xs uppercase tracking-wider text-gray-500 font-bold">{data.dealer_name}</div>
                    <h1 className="text-2xl font-black text-white">{data.vehicle_label}</h1>
                    {staffMode && (
                        <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-[#FF8300]/15 text-[#FF8300] border border-[#FF8300]/40 rounded-sm px-2 py-1" data-testid="portal-staff-banner">
                            <ShieldCheck className="w-3.5 h-3.5" /> Mitarbeiter-Modus — Sicherheitsabfrage übersprungen
                        </div>
                    )}
                    <p className="text-sm text-gray-400 mt-1">Bitte laden Sie die folgenden Unterlagen hoch. Alle Uploads werden sicher übertragen.</p>
                    <div className="mt-4">
                        <div className="flex justify-between text-xs font-bold mb-1"><span className="text-gray-500 uppercase tracking-wider">Fortschritt</span><span className={pct === 100 ? "text-emerald-400" : "text-gray-300"} data-testid="portal-count">{data.done}/{data.total}</span></div>
                        <div className="h-2.5 w-full bg-[#2A2A2E] rounded-full overflow-hidden"><div className={`h-full ${pct === 100 ? "bg-emerald-500" : "bg-[#FF8300]"} transition-all duration-500`} style={{ width: `${pct}%` }} /></div>
                    </div>
                    {pct === 100 && <div className="mt-4 bg-emerald-950/50 border border-emerald-800 rounded-lg p-3 text-sm text-emerald-300 flex items-center gap-2" data-testid="portal-complete"><CheckCircle2 className="w-5 h-5" /> Vielen Dank! Alle Unterlagen wurden hochgeladen.</div>}
                </div>

                <div className="space-y-2 mb-4" data-testid="portal-docs">
                    {data.documents.map((d) => <PortalRow key={d.code} token={token} lastName={lastName} birthDate={birthDate} doc={d} onChanged={setData} highlight={autoDoc && d.code === autoDoc} staffMode={staffMode} />)}
                </div>

                {data.original_required?.length > 0 && (
                    <div className="bg-amber-950/40 border border-amber-800 rounded-xl p-4" data-testid="portal-original-required">
                        <div className="flex items-center gap-2 font-bold text-amber-300 text-sm mb-2"><AlertCircle className="w-4 h-4" /> Bitte zusätzlich im Original mitbringen</div>
                        <ul className="space-y-1">
                            {data.original_required.map((h, i) => (
                                <li key={i} className="text-sm text-amber-200/90 flex items-start gap-2"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span><span className="font-semibold">{h.label}</span> <span className="text-amber-400 text-xs">· {h.source}</span></span></li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}

